import type { CsvRow, ValidatedRow, ValidationLookups, NamedEntity } from '../types/bulkUpload';
import { trimField } from './csvHelpers';

/**
 * Split a multi-value CSV cell into individual tokens.
 *
 * Only ';' is accepted as a separator — the surrounding CSV is comma-delimited,
 * so any literal comma inside a cell is part of the value and must not be
 * treated as a multi-value split.
 */
const parseSemicolonList = (value: string): string[] =>
  value.split(';').map((s) => s.trim()).filter(Boolean);

/** Normalize international prefix so +27… and 0027… are treated as the same number. */
const normalizeMobile = (mobile: string): string =>
  mobile.replace(/^00(\d)/, '+$1');

const validateRow = (
  row: CsvRow,
  index: number,
  seenMobiles: Set<string>,
  seenEmails: Set<string>,
  validationLookups: ValidationLookups,
): ValidatedRow => {
  const errors: string[] = [];
  const { roleNames, featureNames, branchNames, tagNames, ambiguousBranchNames } = validationLookups;

  if (!trimField(row, 'first_name')) {
    errors.push('Missing first name');
  }

  if (!trimField(row, 'last_name')) {
    errors.push('Missing last name');
  }

  const mobile = trimField(row, 'mobile_number').replace(/\s+/g, '');
  if (!mobile) {
    errors.push('Mobile number is required');
  } else if (/^0+$/.test(mobile)) {
    errors.push('Invalid mobile number (all zeros)');
  } else if (!/^\+?[0-9]+$/.test(mobile)) {
    errors.push('Invalid mobile number (must be numeric)');
  }

  const email = trimField(row, 'email');
  if (!email) {
    errors.push('Email is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.push('Invalid email format');
  }

  const emailsEnabled = trimField(row, 'query_and_leave_approval_emails_enabled').toUpperCase();
  if (emailsEnabled && emailsEnabled !== 'TRUE' && emailsEnabled !== 'FALSE') {
    errors.push('Query and leave approval emails enabled must be TRUE or FALSE');
  }

  const lookupChecks: { field: keyof CsvRow; lookup: Set<string>; label: string }[] = [
    { field: 'roles', lookup: roleNames, label: 'role' },
    { field: 'features', lookup: featureNames, label: 'feature' },
    { field: 'receive_queries_from_branches', lookup: branchNames, label: 'branch' },
    { field: 'tags', lookup: tagNames, label: 'tag' },
  ];

  lookupChecks.forEach(({ field, lookup, label }) => {
    const value = row[field];
    if (value?.trim()) {
      const seenInField = new Set<string>();
      parseSemicolonList(value).forEach((v) => {
        const normalized = v.toLowerCase();
        if (seenInField.has(normalized)) {
          errors.push(`Duplicate ${label}: "${v}"`);
          return;
        }
        seenInField.add(normalized);
        if (!lookup.has(normalized)) {
          errors.push(`Unknown ${label}: "${v}"`);
        } else if (field === 'receive_queries_from_branches' && ambiguousBranchNames.has(normalized)) {
          errors.push(`Ambiguous branch: "${v}". Use the branch code instead.`);
        }
      });
    }
  });

  const normalizedMobile = mobile ? normalizeMobile(mobile) : '';
  if (normalizedMobile && seenMobiles.has(normalizedMobile)) {
    errors.push('Duplicate mobile number in file');
  } else if (normalizedMobile) {
    seenMobiles.add(normalizedMobile);
  }

  const emailLower = email.toLowerCase();
  if (emailLower && seenEmails.has(emailLower)) {
    errors.push('Duplicate email in file');
  } else if (emailLower) {
    seenEmails.add(emailLower);
  }

  return {
    row: index + 1,
    data: row,
    errors,
    valid: errors.length === 0,
  };
};

const buildValidationLookups = (
  roles: NamedEntity[],
  features: NamedEntity[],
  branches: NamedEntity[],
  tags: NamedEntity[],
): ValidationLookups => {
  const branchNameCounts = new Map<string, number>();
  branches.forEach((b) => {
    const normalized = (b.name || '').toLowerCase();
    if (normalized) {
      branchNameCounts.set(normalized, (branchNameCounts.get(normalized) || 0) + 1);
    }
  });

  return {
    roleNames: new Set(roles.map((r) => (r.name || '').toLowerCase())),
    featureNames: new Set(features.map((f) => (f.name || '').toLowerCase())),
    branchNames: new Set(branches.flatMap((b) => {
      const entries = [(b.name || '').toLowerCase()];
      if (b.code) entries.push(b.code.toLowerCase());
      entries.push(String(b.id));
      return entries;
    })),
    tagNames: new Set(tags.map((t) => (t.name || '').toLowerCase())),
    ambiguousBranchNames: new Set(
      Array.from(branchNameCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([name]) => name),
    ),
  };
};

const resolveNamesToIds = (
  semicolonString: string | undefined,
  items: NamedEntity[],
  allowIds = false,
): number[] | null => {
  if (!semicolonString || !semicolonString.trim()) return null;

  const idByNameOrCode = new Map<string, number>();
  items.forEach((item) => {
    idByNameOrCode.set((item.name || '').toLowerCase(), item.id);
    if (item.code) idByNameOrCode.set(item.code.toLowerCase(), item.id);
  });

  // Allow the raw entity ID as a token. Registered last and only when the key
  // is still free so an explicit name/code always wins over a same-string ID.
  if (allowIds) {
    items.forEach((item) => {
      const idKey = String(item.id);
      if (!idByNameOrCode.has(idKey)) idByNameOrCode.set(idKey, item.id);
    });
  }

  const tokens = parseSemicolonList(semicolonString);
  const unresolved: string[] = [];
  const ids: number[] = [];

  tokens.forEach((value) => {
    const id = idByNameOrCode.get(value.toLowerCase());
    if (id !== undefined) {
      ids.push(id);
    } else {
      unresolved.push(value);
    }
  });

  if (unresolved.length > 0) {
    throw new Error(`Unresolved values during ID resolution: ${unresolved.join(', ')}`);
  }

  return ids.length > 0 ? ids : null;
};

export { validateRow, buildValidationLookups, resolveNamesToIds };
